(() => {
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();

      this.form = document.querySelector('.collection-filters__form');
      this.grid = document.querySelector('.collection-main__sections');
      this.accordions = this.querySelectorAll('.collection-filters__item');

      this._scrollScheduled = false;

      // --- OPTIMIZATION 1: Create a "debounced" version of the reload function ---
      // This function will only run 350ms after the LAST time it was called,
      // preventing a flood of requests when a user clicks multiple filters.
      this.debouncedReload = this.debounce(this.reloadSections, 350);

      this.setListeners();
      this.initializeScripts();
    }
    
    // --- UTILITY: A standard debounce function ---
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func.apply(this, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    }

    setListeners() {
      this.accordions.forEach(accordion =>
        accordion.addEventListener('click', (e) => this.handleAccordionClick(e))
      );

      // --- CENTRALIZED RELOAD: The form's 'change' event is the single source of truth ---
      // Any change to an input inside the form will trigger the debounced reload.
      this.form?.addEventListener('change', () => {
        this.debouncedReload();
      });

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-filter]');
        if (button) {
          event.preventDefault();
          if (button.classList.contains('price-range')) {
            this.clearPriceRange();
            this.debouncedReload(); // Manually trigger for price range as it's not a form input
          } else {
            // This will uncheck the box, which fires a 'change' event that the listener above will catch.
            this.removeSelectedFilter(button.dataset.filter);
          }
        }
      });

      window.addEventListener('click', (event) => {
        const button = event.target.closest('[data-clear-all-filters]');
        if (button) {
          event.preventDefault();
          event.stopImmediatePropagation();
          // This will uncheck all boxes and fire a 'change' event, which our listener will catch.
          this.clearAllFilters();
        }
      });
    }

    scrollToContainerTop() {
      const container = document.querySelector('.collection-main__container');
      if (!container) return;
      const header = document.querySelector('.header-shopify-section') || document.querySelector('.header') || null;
      const headerH = header ? header.getBoundingClientRect().height : 0;
      const top = container.getBoundingClientRect().top + window.scrollY;
      const targetTop = Math.max(0, top - headerH - 8);
      if (window.scrollY <= targetTop || this._scrollScheduled) return;
      this._scrollScheduled = true;
      requestAnimationFrame(() => {
        window.scrollTo({ top: targetTop, behavior: 'smooth' });
        setTimeout(() => (this._scrollScheduled = false), 450);
      });
    }

    handleAccordionClick(e) {
      const currentAccordion = e.currentTarget;
      const currentContent = currentAccordion.querySelector('.collection-filters__content');
      const isToggleElement = e.target.closest('.collection-filters__header-button');
      if (!isToggleElement || !currentContent) return;
      if (currentAccordion.classList.contains('active')) {
        this.closeAccordion(currentAccordion, currentContent);
        return;
      }
      this.openAccordion(currentAccordion, currentContent);
      currentContent.addEventListener('transitionend', function handler() {
        if (currentAccordion.classList.contains('active')) {
          currentContent.style.maxHeight = 'none';
        }
        currentContent.removeEventListener('transitionend', handler);
      }, { once: true });
    }

    openAccordion(accordion, content) {
      accordion.classList.add('active');
      content.style.maxHeight = content.scrollHeight + 'px';
    }

    closeAccordion(accordion, content) {
      accordion.classList.toggle('active');
      content.style.maxHeight = content.scrollHeight + 'px';
      requestAnimationFrame(() => {
        content.style.maxHeight = '0px';
      });
    }

    // This method is now only called by the form's change listener.
    handleFilterClick() {
      this.reloadSections();
    }

    removeSelectedFilter(filter) {
      const filterButton = this.form.querySelector(`.filter-button[data-filter="${filter}"]:checked`);
      if (filterButton) {
        filterButton.checked = false;
        // Manually dispatch a 'change' event so the form listener picks it up.
        filterButton.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    clearAllFilters() {
      const checkedInputs = this.form.querySelectorAll('.filter-button:checked');
      if (checkedInputs.length > 0) {
        checkedInputs.forEach(item => {
          item.checked = false;
        });
        // Dispatch a single change event from the form itself to trigger the reload.
        this.form.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.clearPriceRange();
    }

    clearPriceRange() {
      const priceRange = this.querySelector('.price-range');
      if (priceRange) {
        this.querySelector('.price-range__input-field--min').value = '';
        this.querySelector('.price-range__input-field--max').value = '';
      }
    }

    // --- MAIN RELOAD LOGIC (PRESERVED FROM ORIGINAL) ---
    reloadSections() {
      this.scrollToContainerTop();
      let urlDefault = '';
      const sections = document.querySelectorAll('collection-slider');
      const formData = new FormData(this.form);
      const searchParams = new URLSearchParams(formData).toString();
      const paramsDefault = new URLSearchParams(searchParams);
      paramsDefault.set('view', 'filter-collection');
      urlDefault = `${location.pathname}?${paramsDefault.toString()}`;
      this.enableLoading();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);
      fetch(urlDefault, {
        signal: controller.signal,
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      })
        .then(response => response.text())
        .then((responseText) => {
          clearTimeout(timeoutId);
          const htmlContent = new DOMParser().parseFromString(responseText, 'text/html');
          const allCollectionSliders = htmlContent.querySelectorAll('collection-slider');
          if (allCollectionSliders.length === sections.length) {
            allCollectionSliders.forEach((fetchedSlider, index) => {
              const existingSlider = sections[index];
              if (existingSlider && fetchedSlider) {
                existingSlider.innerHTML = fetchedSlider.innerHTML;
              }
            });
            this.getSectionsToRender().forEach((section => {
              const fresh = htmlContent.getElementById(section.id);
              if (fresh) {
                const target = document.getElementById(section.id);
                if (target) target.innerHTML = fresh.innerHTML;
              }
            }));
            const dropdownButtonContents = this.querySelectorAll('.collection-filters__header-button');
            dropdownButtonContents.forEach(content => {
              const fresh = htmlContent.getElementById(content.id);
              if (fresh) content.innerHTML = fresh.innerHTML;
            });
            const dropdownContents = this.querySelectorAll('.collection-filters__content-container');
            dropdownContents.forEach(content => {
              const fresh = htmlContent.getElementById(content.id);
              if (fresh) content.innerHTML = fresh.innerHTML;
            });
            this.disableLoading();
            this.reinitAllScripts();

            // Additional drawer reinitialization for safety
            setTimeout(() => this.reinitDrawer(), 100);
            return;
          }
          this.getSectionsToRender().forEach((section => {
            const fresh = htmlContent.getElementById(section.id);
            if (fresh) {
              const target = document.getElementById(section.id);
              if (target) target.innerHTML = fresh.innerHTML;
            }
          }));
          const dropdownButtonContents = this.querySelectorAll('.collection-filters__header-button');
          dropdownButtonContents.forEach(content => {
            const fresh = htmlContent.getElementById(content.id);
            if (fresh) content.innerHTML = fresh.innerHTML;
          });
          const dropdownContents = this.querySelectorAll('.collection-filters__content-container');
          dropdownContents.forEach(content => {
            const fresh = htmlContent.getElementById(content.id);
            if (fresh) content.innerHTML = fresh.innerHTML;
          });
          this.reinitAllScripts();

          // Additional drawer reinitialization for safety
          setTimeout(() => this.reinitDrawer(), 100);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          if (error.name !== 'AbortError') console.error('[Filters] Fetch failed:', error.message);
          this.disableLoading();
        });

      const promises = Array.from(sections).map(section => {
        const collectionHandle = section.dataset.handle;
        const params = new URLSearchParams(searchParams);
        params.set('view', 'filter-collection');
        const url = `/collections/${collectionHandle}?${params.toString()}`;
        return fetch(url)
          .then(response => response.text())
          .then((responseText) => {
            const htmlContent = new DOMParser().parseFromString(responseText, 'text/html');
            const newSection = htmlContent.querySelector('.collection-products-grid > collection-slider');
            if (newSection) section.replaceWith(newSection);
          })
          .catch((error) => {
            console.error('Fetch failed for section', section, error);
          });
      });

      Promise.all(promises).finally(() => {
        this.disableLoading();
        this.reinitAllScripts();

        // Additional drawer reinitialization for safety
        setTimeout(() => this.reinitDrawer(), 100);
      });
    }

    // Helper to run all re-initialization scripts
    reinitAllScripts() {
        const tabsComponents = document.querySelectorAll('collection-tabs');
        if (tabsComponents.length > 0) {
          tabsComponents.forEach(tabsComponent => {
            // This is an aggressive way to re-init, but we will preserve it from the original code
            tabsComponent.replaceWith(tabsComponent.cloneNode(true));
          });
        }

        // Reinitialize drawer functionality
        this.reinitDrawer();

        this.reinitJudgeMeFormatting();
        this.reinitStickyTabs();
        this.reinitMobileTabsAlignment();
    }

    // Reinitialize drawer functionality after AJAX updates
    reinitDrawer() {
        // Find the drawer element
        const drawerElement = document.querySelector('collection-drawer');
        if (!drawerElement) {
            return;
        }

        // Check if the drawer has the required properties and methods
        if (typeof drawerElement.initMutationObserver === 'function') {
            // Disconnect any existing observers first
            if (drawerElement._drawerObserver) {
                drawerElement._drawerObserver.disconnect();
                drawerElement._drawerObserver = null;
            }

            // Update the button reference first
            const newButton = document.querySelector('.collection-drawer__button-open');
            if (newButton) {
                // Update the button reference
                drawerElement.toggleBtn = newButton;

                // Note: Click event listener is now handled in initMutationObserver()
                // which will be called next, so no need to add it here
            }

            // Reinitialize the drawer mutation observer
            drawerElement.initMutationObserver();
        }
    }

    getSectionsToRender() { return [{ id: 'active-filters-mob' }, { id: 'active-filters' }]; }
    enableLoading() { this.grid?.classList.add('loading'); }
    disableLoading() { this.grid?.classList.remove('loading'); }

    // All original re-initialization logic is preserved below, with guards removed
    // to ensure they can run successfully multiple times.

    reinitJudgeMeFormatting() {
      this.applyJudgeMeFormatting();
      setTimeout(() => this.applyJudgeMeFormatting(), 150);
      setTimeout(() => this.applyJudgeMeFormatting(), 300);
      setTimeout(() => this.applyJudgeMeFormatting(), 1200);
    }
    applyJudgeMeFormatting() {
      const badgeTexts = document.querySelectorAll('.collection-products-slider__review .jdgm-prev-badge__text');
      const summaryTexts = document.querySelectorAll('.jdgm-rev-widg__summary-text');
      badgeTexts.forEach(badgeText => {
        if (badgeText.innerHTML.includes('reviews') || badgeText.innerHTML.includes('review')) {
          const badgeElement = badgeText.closest('.jdgm-prev-badge');
          if (badgeElement) {
            const rating = badgeElement.getAttribute('data-average-rating');
            if (rating) {
              badgeText.innerHTML = parseFloat(rating).toFixed(1);
            }
          }
        }
      });
      summaryTexts.forEach(summaryText => {
        if (summaryText.innerHTML && !summaryText.innerHTML.includes(',') && /\d/.test(summaryText.innerHTML)) {
          const numMainReviewsClean = summaryText.innerHTML.replace(/,/g, "");
          const numMainReviewsComma = this.numberWithCommas(numMainReviewsClean);
          summaryText.innerHTML = summaryText.innerHTML.replace(numMainReviewsClean, numMainReviewsComma);
        }
      });
    }
    numberWithCommas(x) { return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ","); }

    initializeScripts() {
      this.reinitStickyTabs();
      this.reinitMobileTabsAlignment();
    }

    reinitStickyTabs() {
      // The one-time guard is removed. This function can now run again.
      const mql = window.matchMedia('(max-width: 1023px)');
      const stickApp = document.querySelector('.gta-widget');
      const headerEl = document.querySelector('.header-shopify-section');
      const mobileTabs = document.querySelector('.collection-main-tabs.mobile-only');
      const desktopTabs = document.querySelector('.collection-main-tabs-desktop');
      //const getHeaderHeight = () => (headerEl ? headerEl.offsetHeight : 0);
      const getHeaderHeight = () => {
        let totalHeight = 0;
        // Only add height if element exists on the page
        // Note: topBar is already included in headerEl (.header-shopify-section)
        if (headerEl) totalHeight += headerEl.offsetHeight || 0;
        if (stickApp) totalHeight += stickApp.offsetHeight || 0;
        return totalHeight;
      };
      const getTarget = () => (mql.matches ? mobileTabs : desktopTabs);
      let ticking = false;
      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          const target = getTarget();
          if (!target) { ticking = false; return; }
          const shouldStick = window.scrollY > 0;
          target.classList.toggle('top-sticky', shouldStick);
          if (shouldStick) {
            document.documentElement.style.setProperty('--section-header-height', `${getHeaderHeight()}px`);
          } else {
            document.documentElement.style.removeProperty('--section-header-height');
            document.documentElement.style.paddingTop = '0px';
          }
          ticking = false;
        });
      };
      const onResizeOrChange = () => {
        const target = getTarget();
        if (!target) return;
        if (target.classList.contains('top-sticky')) {
          document.documentElement.style.setProperty('--section-header-height', `${getHeaderHeight()}px`);
        }
        onScroll();
      };

      // To prevent duplicate listeners, we remove the old one if it exists before adding a new one.
      if (window._stickyOnScroll) window.removeEventListener('scroll', window._stickyOnScroll);
      if (window._stickyOnResize) window.removeEventListener('resize', window._stickyOnResize);
      if (window._stickyMql) window._stickyMql.removeEventListener('change', window._stickyOnResize);
      
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onResizeOrChange);
      if (mql.addEventListener) mql.addEventListener('change', onResizeOrChange);
      
      // Store references for cleanup on the next run
      window._stickyOnScroll = onScroll;
      window._stickyOnResize = onResizeOrChange;
      window._stickyMql = mql;

      onResizeOrChange();
    }

    reinitMobileTabsAlignment() {
      // The one-time guard is removed. This complex function is now allowed to run again.
      // Its internal cleanup logic will handle the re-initialization.
      const MQ = window.matchMedia('(max-width: 1023px)');

      const alignActive = (tabsEl) => {
        if (!MQ.matches) return;

        const scroller = tabsEl.querySelector('.collection-main__nav');
        if (!scroller) {
          return;
        }

        const active = scroller.querySelector('.collection-main-selector.active');
        if (!active) {
          return;
        }

        // Cache items if not already cached
        if (!tabsEl._cachedItems) {
          tabsEl._cachedItems = Array.from(scroller.querySelectorAll('.collection-main-selector'));
        }
        const items = tabsEl._cachedItems;

        if (!items.length) return;

        const activeIndex = items.indexOf(active);
        if (activeIndex === -1) {
          return;
        }

        // Calculate scroll position based on active tab
        const activeRect = active.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();

        // Calculate how much to scroll to center the active tab
        const activeCenter = activeRect.left + activeRect.width / 2;
        const scrollerCenter = scrollerRect.left + scrollerRect.width / 2;
        const scrollOffset = activeCenter - scrollerCenter;

        // Account for filter button space (if present)
        const filterButton = tabsEl.querySelector('.collection-drawer__buttons');
        let buttonOverlap = 0;
        if (filterButton) {
          const buttonRect = filterButton.getBoundingClientRect();
          buttonOverlap = Math.max(0, (buttonRect.right - scrollerRect.right) + 16);
        }

        // Calculate target scroll position with better centering
        const currentScroll = scroller.scrollLeft;
        let targetScroll = currentScroll + scrollOffset;

        // Ensure we don't scroll beyond bounds
        const maxScroll = scroller.scrollWidth - scroller.clientWidth;
        targetScroll = Math.max(0, Math.min(targetScroll, maxScroll));

        // Only scroll if the active tab is not already visible
        const activeLeft = activeRect.left - scrollerRect.left + currentScroll;
        const activeRight = activeLeft + activeRect.width;
        const scrollerWidth = scrollerRect.width - buttonOverlap;

        if (activeLeft < 0 || activeRight > scrollerWidth) {
          // Use smooth scrolling for better UX
          scroller.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
        }

        tabsEl._lastActiveIndex = activeIndex;
      };

      const setupTabs = (tabsEl) => {
        let raf = 0;
        let lastActiveIndex = -1;

        const schedule = () => {
          if (!MQ.matches || raf) {
            return;
          }

          const active = tabsEl.querySelector('.collection-main-selector.active');
          if (!active) {
            return;
          }

          const items = tabsEl._cachedItems || Array.from(tabsEl.querySelectorAll('.collection-main-selector'));
          const currentIndex = items.indexOf(active);

          if (currentIndex === lastActiveIndex) {
            return;
          }
          lastActiveIndex = currentIndex;

          raf = requestAnimationFrame(() => {
            raf = 0;
            alignActive(tabsEl);
          });
        };

        // Optimized MutationObserver - only watch for class changes on selectors
        const mo = new MutationObserver((mutations) => {
          let shouldSchedule = false;
          for (const mutation of mutations) {
            if (mutation.type === 'attributes' &&
                mutation.attributeName === 'class' &&
                mutation.target.classList.contains('collection-main-selector')) {
              shouldSchedule = true;
              break;
            }
          }
          if (shouldSchedule) {
            schedule();
          }
        });

        mo.observe(tabsEl, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class']
        });
        tabsEl._mobileTabsObserver = mo;

        // Throttled scroll handler with better performance
        let scrollTimeout;
        const scrollHandler = () => {
          clearTimeout(scrollTimeout);
          scrollTimeout = setTimeout(schedule, 16); // ~60fps
        };
        window.addEventListener('scroll', scrollHandler, { passive: true });
        tabsEl._mobileTabsScrollHandler = scrollHandler;

        // Improved resize handler
        let resizeTimeout;
        const onResize = () => {
          clearTimeout(resizeTimeout);
          resizeTimeout = setTimeout(() => {
            // Clear cache on resize for accurate measurements
            tabsEl._cachedItems = null;

            if (!MQ.matches) {
              const scroller = tabsEl.querySelector('.collection-main__nav');
              if (scroller) scroller.scrollLeft = 0;
            } else {
              schedule();
            }
          }, 100);
        };
        window.addEventListener('resize', onResize, { passive: true });
        if (MQ.addEventListener) MQ.addEventListener('change', onResize);
        tabsEl._mobileTabsResizeHandler = onResize;

        // Initial alignment
        schedule();
      };

      // Clean up existing observers first
      const existingMobileTabs = document.querySelectorAll('collection-tabs.mobile-only');
      existingMobileTabs.forEach(tabsEl => {
        if (tabsEl._mobileTabsObserver) {
          tabsEl._mobileTabsObserver.disconnect();
          tabsEl._mobileTabsObserver = null;
        }
        if (tabsEl._mobileTabsScrollHandler) {
          window.removeEventListener('scroll', tabsEl._mobileTabsScrollHandler);
          tabsEl._mobileTabsScrollHandler = null;
        }
        if (tabsEl._mobileTabsResizeHandler) {
          window.removeEventListener('resize', tabsEl._mobileTabsResizeHandler);
          if (MQ.removeEventListener) MQ.removeEventListener('change', tabsEl._mobileTabsResizeHandler);
          tabsEl._mobileTabsResizeHandler = null;
        }
        // Clear cache
        tabsEl._cachedItems = null;
        tabsEl._lastActiveIndex = null;
      });

      // Initialize only mobile tabs
      const newMobileTabs = document.querySelectorAll('collection-tabs.mobile-only');
      newMobileTabs.forEach((tabsEl, index) => {
        setupTabs(tabsEl);
      });
    }
  }
  customElements.define('collection-filters', CollectionFilters);
})();